import { BackArrow } from "../Icons/Icons"
import Dropdown from "../Dropdown/Dropdown"
import Input from "../Input/Input"
import Calendar from "../Calendar/Calendar"
import DateTimeField from "../Calendar/DateTimeField"
import Button from "../Button/Button"
import React, { useEffect, useState } from "react"
import dayjs from "dayjs"
import TextArea from "../TextArea/TextArea"
import { useDispatch } from "react-redux"
import { showSnackBar } from "../Snackbar/Snackbar"
import { getTaskByIdReq, getTaskPeopleReq, getTaskStatuses, getTaskTemplatesReq, updateTaskReq } from "../../features/tasks/tasks"
import { clearListCache } from "../../utils/listCache"
import { useMyContextLoading } from "../LoadingContextProvider/LoadingContextProvider"
import { getBaseUserById } from "../../features/user/user"
import { getAssignToInfo } from "../../features/customer/customerSlice"
import { now, dateToServer, dateTimeToServer, isRealDate, serverToLocal, userTimeZone } from "../../utils/date"
import { ReminderStatusChip } from "./TaskChip"
import { useTaskFlags } from "./useTaskFlags"
import {
  CURRENT_ASSIGNEE_LABEL, MAX_REMINDER_RECIPIENTS, NOTIFY_REQUIRED_MESSAGE, RECIPIENT_LIMIT_MESSAGE, formatReminderTime, sameIds
} from "./taskReminders"

const NO_PEOPLE = []

const EditTask = ({ setActiveTab, taskId, isTasksPage, setIsUpdated, isUpdated }) => {
  const [isError, setIsError] = useState(false)
  const [title, setTitle] = useState('')
  const [assignedTo, setAssignedTo] = useState('')
  const [description, setDescription] = useState('')
  const [dueDate, setDueDate] = useState(now())
  const [dropdownData, setDropdownData] = useState([])
  const [status, setStatus] = useState()
  const [loadedStatus, setLoadedStatus] = useState()
  const [assignees, setAssignees] = useState([])
  const [remindAt, setRemindAt] = useState(null)
  const [recipients, setRecipients] = useState(null)
  const [reminder, setReminder] = useState(null)
  const [leadId, setLeadId] = useState(null)
  const [people, setPeople] = useState(NO_PEOPLE)
  const [personTask, setPersonTask] = useState(false)
  const dispatch = useDispatch()
  const flags = useTaskFlags()
  const { fixesOn, peopleMode, viewer, viewerReady } = flags
  const reminderUiOn = flags.reminderUiOn && !personTask
  const fixedForm = fixesOn || peopleMode
  const scheduled = reminder?.status === 'scheduled'
  const notify = recipients ?? (people.some((person) => person.value === viewer?._id) ? [viewer._id] : NO_PEOPLE)
  const { triggerLoading } = useMyContextLoading()
  const [templateData, setTemplateData] = useState([])
  const [template, setTemplate] = useState("")
  const [trigger, setTrigger] = useState(false)

  const [userData, setUserData] = useState()
  const getTasksStatus = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(getTaskStatuses())
      setDropdownData(response?.payload?.data)
    } catch (error) {
      showSnackBar("error", "Failed to fetch task statuses")
    }
  }

  const getTaskById = async () => {
    try {
      const response = await dispatch(getTaskByIdReq(taskId))
      if (response?.payload?.data) {
        const { title, user, description, due, status, assignedTo: taskAssignee, reminder: taskReminder } = response?.payload?.data
        const assigneeId = typeof taskAssignee === 'string' ? taskAssignee : taskAssignee?._id

        setTitle(title)
        setAssignedTo(fixedForm ? (assigneeId || '') : user)
        setDescription(description)
        setDueDate(serverToLocal(due))
        setStatus(fixedForm ? status?._id : status?.value)
        fixedForm && setLoadedStatus(status?._id)
        peopleMode && setLeadId(typeof user === 'string' ? user : user?._id)
        if (reminderUiOn) {
          setReminder(taskReminder || null)
          if (taskReminder?.status === 'scheduled') {
            setRemindAt(serverToLocal(taskReminder.at))
            setRecipients(taskReminder.recipients || NO_PEOPLE)
          }
        }

        if (!fixedForm) {
          await getUserInfo({ userId: typeof user === 'string' ? user : user?._id })
        } else if (assigneeId && !peopleMode) {
          await getUserInfo({ userId: assigneeId })
        }
      }
    } catch (error) {
      showSnackBar("error", "Failed to fetch task")
    }
    triggerLoading(false)
  }

  const reminderChange = () => {
    if (!reminderUiOn) return {}
    if (!remindAt) return scheduled ? { reminder: null } : {}
    const unchanged = scheduled
      && dayjs(remindAt).valueOf() === dayjs(reminder.at).valueOf()
      && sameIds(notify, reminder.recipients)
    return unchanged ? {} : { reminder: { at: dateTimeToServer(remindAt), recipients: notify, timeZone: userTimeZone } }
  }

  const updateTask = async () => {
    try {
      if (!title || !description || !dueDate || !assignedTo) {
        setIsError(true)
        showSnackBar("error", `${!title ? "Title" : ''} ${!dueDate ? "Due Date" : ''} ${!assignedTo ? "Assigned To" : ''} are required fields`)
        return
      }
      if (reminderUiOn && remindAt && !isRealDate(remindAt)) {
        setIsError(true)
        showSnackBar("error", "Remind at is not a valid date and time")
        return
      }
      if (reminderUiOn && remindAt && notify.length === 0) {
        showSnackBar("error", NOTIFY_REQUIRED_MESSAGE)
        return
      }

      const selectedId = fixedForm ? status : dropdownData.find((e) => e?.value === status)?._id
      const statusField = fixedForm && status === loadedStatus ? {} : { status: selectedId }
      const data = {
        title, description, due: dateToServer(dueDate), ...statusField, assignedTo, ...reminderChange()
      }


      const response = await dispatch(updateTaskReq({ taskId, data }))
      if (response?.payload?.data?._id) {
        setIsUpdated(!isUpdated)
        clearListCache('tasks')
        setActiveTab("tasks")
        showSnackBar("success", "Task updated successfully")
      } else {
        showSnackBar("error", peopleMode && typeof response?.payload === 'string' && response.payload ? response.payload : "Something going wrong, try later")
      }
    } catch (error) {
      showSnackBar("error", "Failed to update task")
    }
  }

  const getTemplates = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(getTaskTemplatesReq())
      setTemplateData(response?.payload?.data)
    } catch (error) {
      showSnackBar("error", "Failed to fetch templates")
    }
    triggerLoading(false)
  }
  const handleDropdown = async (e) => {
    setTemplate(e)
  }

  const getUserInfo = async ({ userId }) => {
    const resultAction = await dispatch(getBaseUserById(userId))
    const user = resultAction.payload.data
    if (user) {
      setUserData(user)
    } else {
      showSnackBar("error", "Failed to fetch user info")
    }
  }

  const getAssignees = async () => {
    try {
      const response = await dispatch(getAssignToInfo())
      if (response?.error) {
        showSnackBar("error", "Failed to fetch users")
        return
      }
      setAssignees((response?.payload?.data || []).map(person => ({
        label: person.firstName + " " + person.lastName,
        value: person._id
      })))
    } catch (error) {
      showSnackBar("error", "Failed to fetch users")
    }
  }

  const changeNotify = (next) => {
    if (next.length > MAX_REMINDER_RECIPIENTS) {
      showSnackBar("error", RECIPIENT_LIMIT_MESSAGE)
      return
    }
    setRecipients(next)
  }

  useEffect(() => {
    if (!viewerReady) return
    getTaskById()
    getTasksStatus()
    getTemplates()
    fixedForm && !peopleMode && getAssignees()
  }, [viewerReady])

  useEffect(() => {
    if (!peopleMode || !leadId) return undefined
    let current = true
    dispatch(getTaskPeopleReq(leadId)).then((response) => {
      if (!current) return
      if (response?.meta?.status === 404) {
        setPersonTask(true)
        getAssignees()
        return
      }
      if (response?.error) {
        showSnackBar("error", typeof response.payload === 'string' && response.payload ? response.payload : "Failed to fetch people")
        return
      }
      setPeople((response?.payload?.data || NO_PEOPLE).map(({ value, label }) => ({ value, label })))
    })
    return () => {
      current = false
    }
  }, [peopleMode, leadId, dispatch])

  useEffect(() => {
    templateData.map((e) => {
      if (e?.value === template) {
        setDescription(e?.description)
        setTitle(e?.label)
        setTrigger(!trigger)
      }
    })
  }, [template])

  const assigneeChoices = peopleMode && !personTask ? people : assignees
  const missingAssigneeOption = peopleMode
    ? assignedTo && assigneeChoices.length > 0 && !assigneeChoices.some((person) => person.value === assignedTo)
      && { label: CURRENT_ASSIGNEE_LABEL, value: assignedTo }
    : userData && !assigneeChoices.some((person) => person.value === userData._id)
      && { label: userData.firstName + " " + userData.lastName, value: userData._id }
  const assigneeOptions = missingAssigneeOption ? [missingAssigneeOption, ...assigneeChoices] : assigneeChoices

  return (
    <div className={`flex px-6 w-full flex-col gap-6 mb-6 ${isTasksPage && "justify-center items-center"}`}>
      <div className="w-full  flex flex-col gap-8">
        {isTasksPage ?
          <div className="flex items-center justify-start gap-2 cursor-pointer" onClick={() => setActiveTab("tasks")}>

            <h5 className="font-bold text-xl">Edit Task</h5>
          </div>
          :
          <div className="flex items-center justify-start gap-2 cursor-pointer" onClick={() => setActiveTab("tasks")}>
            <div className="h-6 w-6 flex items-center justify-center">
              <BackArrow />
            </div>
            <h5 className="font-bold text-xl-20">Edit Task</h5>
          </div>}
      </div>
      <div
        className={`flex flex-col gap-6 px-6 py-5 rounded-2xl shadow-lg border border-divider w-full ${isTasksPage && "max-w-[898px]"}`}>
        <Dropdown value={template} menuItems={templateData} setValue={handleDropdown} label="Template" />
        <Input errorMsg={isError && !title} placeholder={"Enter title"} label={"Title"} value={title}
          onChange={(e) => setTitle(e.target.value)} />
        <TextArea text={description} setText={setDescription} placeholder="Enter Description" trigger={trigger} />
        <div className="flex items-end gap-4">
          <div className="w-full">
            <Calendar
              width="w-full"
              label="Due Date"
              value={dueDate}
              onChange={(date) => setDueDate(date)}
            />
          </div>
          <div className="w-full">
            {fixedForm ?
              <Dropdown label="Assign To" placeholder="Select a Value" menuItems={assigneeOptions}
                menuFitsField={peopleMode && !personTask} value={assignedTo} setValue={setAssignedTo} />
              :
              <Dropdown label="Assign To"
                menuItems={[{ label: userData?.firstName + " " + userData?.lastName, value: assignedTo }]}
                value={userData?._id} onChange={(value) => setAssignedTo(value)} />}
          </div>
        </div>
        {reminderUiOn && (
          <div className="flex items-start gap-4">
            <div className="w-full flex flex-col gap-2">
              <DateTimeField width="w-full" label="Remind at" value={remindAt} onChange={setRemindAt} />
              {scheduled && remindAt && (
                <div>
                  <Button type="text" size="sm" onClick={() => setRemindAt(null)}>Remove reminder</Button>
                </div>
              )}
              {reminder?.status && !scheduled && (
                <div className="flex items-center gap-2 text-[13px] text-text-secondary">
                  <span>Last reminder</span>
                  <ReminderStatusChip status={reminder.status} />
                  <span>{formatReminderTime(reminder.at)}</span>
                </div>
              )}
            </div>
            <div className="w-full">
              <Dropdown label="Notify" ariaLabel="Notify" placeholder="Select people" isMulti menuFitsField
                helperText={`Up to ${MAX_REMINDER_RECIPIENTS} people`}
                menuItems={people} value={people.length ? notify : NO_PEOPLE} setValue={changeNotify} />
            </div>
          </div>
        )}
        <Dropdown label="Status" className="pb-6" menuItems={dropdownData} value={status}
          setValue={(e) => setStatus(e)} />
        <div className="flex justify-end items-center gap-4 self-stretch py-6 px-0 border-t border-divider ">
          <Button onClick={() => setActiveTab("tasks")} type="outlined" size="lg" width="w-[156px]">Cancel</Button>
          <Button
            size="lg" width="w-[156px]"
            onClick={() => updateTask()}>Save</Button>
        </div>
      </div>
    </div>
  )
}
export default EditTask